import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowDown, ArrowUp, Plus, Trash2, ShieldCheck, Lock, ClipboardList, FileText, Download, BarChart3, Key, Calendar, User, MessageSquare, Database, Mail, BookOpen, Users } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { API } from '../config/api';
import { desktopApi, mailFailureText, mailOutcomeToast, mailStatusText } from '../lib/desktopApi';
import { formatDateTime24 } from '../lib/time';
import ConsignesEditor from '../components/admin/ConsignesEditor';
import StudentsAdmin from '../components/admin/StudentsAdmin';

const MAIL_TAGS = [
  { id: 'date_debut', hint: 'Début de service, JJ.MM.AAAA' },
  { id: 'date_fin', hint: 'Fin de service, JJ.MM.AAAA' },
  { id: 'initiales', hint: 'Initiales de l\'agent qui envoie' },
  { id: 'agent', hint: 'Nom complet de l\'agent qui envoie' },
  { id: 'date_envoi', hint: 'Date d\'envoi, JJ.MM.AAAA' },
];

function renderMailTemplate(template, context) {
  return String(template || '').replace(/\{([^{}]+)\}/g, (_, raw) => {
    const name = String(raw || '').trim();
    if (!Object.prototype.hasOwnProperty.call(context, name)) return `{${name}}`;
    return context[name] ?? '';
  });
}

function safePreviewFilename(rendered) {
  const cleaned = String(rendered || '').replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();
  const base = cleaned || 'rapport';
  return base.toLowerCase().endsWith('.pdf') ? base : `${base}.pdf`;
}

const AdminPanel = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [manoeuvres, setManoeuvres] = useState([]);
  const [reports, setReports] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newAgentName, setNewAgentName] = useState('');
  const [authMode, setAuthMode] = useState('password');
  const [credentialDrafts, setCredentialDrafts] = useState({});
  const [loginAudit, setLoginAudit] = useState([]);
  const [newManoeuvreName, setNewManoeuvreName] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminToken, setAdminToken] = useState(null);
  const [password, setPassword] = useState('');
  const [adminUsername, setAdminUsername] = useState('');
  const [accountId, setAccountId] = useState('');
  const [showPasswordDialog, setShowPasswordDialog] = useState(true);
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [showMigrationDialog, setShowMigrationDialog] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [validationResult, setValidationResult] = useState(null);
  const [appInfo, setAppInfo] = useState(null);
  const [zipMonth, setZipMonth] = useState(new Date().toISOString().slice(0, 7));
  
  // Filtres rapports
  const [filterAgent, setFilterAgent] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({ agent: '', from: '', to: '' });
  const [reportPage, setReportPage] = useState(0);
  const [reportTotal, setReportTotal] = useState(0);
  const reportPageSize = 20;
  
  // Changement mot de passe
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Message d'alerte dashboard
  const [alertTitle, setAlertTitle] = useState('');
  const [alertMessage, setAlertMessage] = useState('');

  // Paramètres établissement
  const [establishmentName, setEstablishmentName] = useState('');
  const [establishmentSubtitle, setEstablishmentSubtitle] = useState('');
  const [defaultShiftStart, setDefaultShiftStart] = useState('');
  const [defaultShiftEnd, setDefaultShiftEnd] = useState('');
  const [logoPath, setLogoPath] = useState('');
  const [heroImagePath, setHeroImagePath] = useState('');
  // Éditeur des zones du rapport
  const ZONE_DEFS = [
    { id: 'ecoles', key: 'ecoles', label: 'Contrôle Salles École', hasRooms: true, hasItems: false },
    { id: 'etage2', key: 'etage2', label: '2ème Étage', hasRooms: true, hasItems: false },
    { id: 'reception', key: 'reception', label: 'Zone Réception', hasRooms: true, hasItems: false },
    { id: 'reception_bureau', key: 'reception', label: 'Zone Réception — Bureau direction', hasRooms: false, hasItems: false, optionsField: 'bureau_options' },
    { id: 'reception_fenetres', key: 'reception', label: 'Zone Réception — Fenêtres coulissantes', hasRooms: false, hasItems: false, optionsField: 'fenetres_options' },
    { id: 'restaurant', key: 'restaurant', label: 'Zone Restaurant', hasRooms: false, hasItems: true },
    { id: 'cuisine', key: 'cuisine', label: 'Zone Cuisine', hasRooms: false, hasItems: true },
    { id: 'sous_sols', key: 'sous_sols', label: 'Sous-sols', hasRooms: false, hasItems: true },
  ];
  const [reportConfig, setReportConfig] = useState(null);
  const [remarkTemplates, setRemarkTemplates] = useState(null);
  const [newRemarkInput, setNewRemarkInput] = useState({});
  const [newRemarkCategory, setNewRemarkCategory] = useState('');
  const [newOptionInput, setNewOptionInput] = useState({});
  const [newRoomInput, setNewRoomInput] = useState({});
  const [newItemInput, setNewItemInput] = useState({});

  // Réceptionnistes
  const [receptionistes, setReceptionistes] = useState([]);
  const [newReceptioniste, setNewReceptioniste] = useState('');
  const [mailConfig, setMailConfig] = useState(null);
  const [mailForm, setMailForm] = useState(null);
  const [mailAddress, setMailAddress] = useState('');
  const [mailPassword, setMailPassword] = useState('');
  const [replacePassword, setReplacePassword] = useState(false);
  const [mailTestTo, setMailTestTo] = useState('');
  const [mailTestResult, setMailTestResult] = useState('');
  const [mailEditId, setMailEditId] = useState('');
  const [mailEditAddress, setMailEditAddress] = useState('');
  const [mailSaving, setMailSaving] = useState(false);
  const mailFocus = useRef(null);
  const adminSections = ['agents', 'login-audit', 'receptionistes', 'manoeuvres', 'mail', 'reports', 'alert', 'establishment', 'ops', 'password', 'database', 'version'];
  const [openSections, setOpenSections] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
      ? ['reports', 'alert', 'establishment']
      : adminSections
  ));

  useEffect(() => {
    setShowPasswordDialog(true);
    setIsAuthenticated(false);
    setAdminToken(null);
    setAccountId('');
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      desktopApi.getAuthConfig().then(async (config) => {
        const mode = config?.mode || 'open';
        setAuthMode(mode);
        if (mode === 'password' && adminToken) {
          const data = await desktopApi.getLoginAudit(adminToken, 100);
          setLoginAudit(Array.isArray(data) ? data : []);
        }
      }).catch(() => null);
      fetchAgents();
      fetchManoeuvres();
      if (adminToken) fetchStatistics();
      fetchAlertSettings();
      fetchEstablishmentSettings();
      fetchReportConfig();
      fetchRemarkTemplates();
      fetchAppInfo();
      fetchReceptionistes();
      if (adminToken) fetchMigrationStatus();
      if (adminToken) fetchAuditLog();
      if (adminToken) fetchLoginAudit();
      if (adminToken) fetchMailSettings();
    }
  }, [isAuthenticated, adminToken]);

  useEffect(() => {
    const onExpired = () => {
      setAdminToken(null);
      setIsAuthenticated(false);
      setShowPasswordDialog(true);
      toast.error('Session expirée');
    };
    window.addEventListener('vatel-admin-expired', onExpired);
    return () => window.removeEventListener('vatel-admin-expired', onExpired);
  }, []);

  const handlePasswordSubmit = async () => {
    try {
      const response = await desktopApi.verifyAdmin(password, adminUsername);
      if (response.valid) {
        setIsAuthenticated(true);
        setAdminToken(response.token);
        setAccountId(response.id || '');
        setShowPasswordDialog(false);
        toast.success('Accès autorisé');
      } else {
        toast.error('Mot de passe incorrect');
      }
    } catch (error) {
      toast.error('Erreur de vérification');
    }
  };

  const fetchMigrationStatus = async () => {
    if (!adminToken) return;
    try {
      const data = await desktopApi.getMigrationStatus(adminToken);
      setMigrationStatus(data);
      if (data?.legacyFound || data?.migrated || data?.error) {
        setShowMigrationDialog(true);
      }
    } catch (error) {
      console.error('Erreur migration status:', error);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await desktopApi.getAgents(adminToken);
      setAgents(response);
    } catch (error) {
      toast.error('Erreur lors du chargement des agents');
    }
  };

  const fetchManoeuvres = async () => {
    try {
      const response = await desktopApi.getManoeuvres(adminToken);
      setManoeuvres(response);
    } catch (error) {
      toast.error('Erreur lors du chargement des manœuvres');
    }
  };

  const fetchMailSettings = async () => {
    try {
      const data = await desktopApi.getMailSettings(adminToken);
      setMailConfig(data);
    } catch (error) {
      toast.error("Impossible de charger l'envoi par e-mail");
    }
  };

  useEffect(() => {
    if (!mailConfig) return;
    setMailForm({
      enabled: !!mailConfig.enabled,
      host: mailConfig.host || '',
      port: String(mailConfig.port || 587),
      security: mailConfig.security === 'ssl' ? 'ssl' : 'starttls',
      user: mailConfig.user || '',
      from_name: mailConfig.from_name || '',
      from_address: mailConfig.from_address || '',
      reply_to: mailConfig.reply_to || '',
      subject_template: mailConfig.subject_template || '',
      filename_template: mailConfig.filename_template || '',
      body_template: mailConfig.body_template || '',
    });
    setMailTestTo((current) => current || mailConfig.default_test_address || '');
  }, [mailConfig]);

  const patchMailForm = (field, value) => {
    setMailForm((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const insertMailTag = (tag) => {
    const token = `{${tag}}`;
    const field = mailFocus.current?.dataset?.mailField || 'subject_template';
    setMailForm((prev) => {
      if (!prev) return prev;
      const current = String(prev[field] || '');
      const el = mailFocus.current;
      if (!el || el.dataset.mailField !== field) {
        return { ...prev, [field]: `${current}${token}` };
      }
      const start = el.selectionStart ?? current.length;
      const end = el.selectionEnd ?? start;
      return { ...prev, [field]: `${current.slice(0, start)}${token}${current.slice(end)}` };
    });
  };

  const handleSaveMailSettings = async () => {
    if (!mailForm) return;
    setMailSaving(true);
    try {
      const payload = {
        enabled: !!mailForm.enabled,
        host: mailForm.host.trim(),
        port: Number(mailForm.port),
        security: mailForm.security,
        user: mailForm.user.trim(),
        from_name: mailForm.from_name,
        from_address: mailForm.from_address.trim(),
        reply_to: mailForm.reply_to.trim(),
        subject_template: mailForm.subject_template,
        filename_template: mailForm.filename_template,
        body_template: mailForm.body_template,
      };
      if (replacePassword && mailPassword.trim()) payload.password = mailPassword;
      const data = await desktopApi.updateMailSettings(payload, adminToken);
      setMailConfig(data);
      setMailPassword('');
      setReplacePassword(false);
      toast.success('Réglages d\'envoi enregistrés');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Enregistrement impossible');
    } finally {
      setMailSaving(false);
    }
  };

  const handleRestoreMailDefaults = () => {
    const defaults = mailConfig?.defaults || {};
    setMailForm((prev) => (prev ? {
      ...prev,
      from_name: defaults.from_name || prev.from_name,
      subject_template: defaults.subject_template || prev.subject_template,
      filename_template: defaults.filename_template || prev.filename_template,
      body_template: defaults.body_template || prev.body_template,
    } : prev));
  };

  const handleAddRecipient = async () => {
    const address = mailAddress.trim();
    if (!address) return;
    try {
      await desktopApi.addMailRecipient(address, adminToken);
      setMailAddress('');
      toast.success('Adresse ajoutée');
      fetchMailSettings();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Adresse refusée');
    }
  };

  const handleRemoveRecipient = async (recipientId) => {
    try {
      await desktopApi.deleteMailRecipient(recipientId, adminToken);
      toast.success('Adresse retirée');
      fetchMailSettings();
    } catch (error) {
      toast.error('Suppression impossible');
    }
  };

  const handleUpdateRecipient = async () => {
    if (!mailEditId || !mailEditAddress.trim()) return;
    try {
      await desktopApi.updateMailRecipient(mailEditId, mailEditAddress.trim(), adminToken);
      setMailEditId('');
      setMailEditAddress('');
      toast.success('Adresse modifiée');
      fetchMailSettings();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Modification refusée');
    }
  };

  const handleTestMail = async () => {
    try {
      const result = await desktopApi.sendTestMail(adminToken, mailTestTo.trim());
      const text = result?.message || 'E-mail de test envoyé';
      setMailTestResult(text);
      toast.success(text);
      fetchMailSettings();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const text = typeof detail === 'string' ? detail : "L'e-mail de test a échoué";
      setMailTestResult(text);
      toast.error(text, { duration: 12000 });
      fetchMailSettings();
    }
  };

  const handleResendMail = async (reportId) => {
    try {
      const result = await desktopApi.resendReportMail(reportId, adminToken);
      mailOutcomeToast(toast, result?.mail);
      fetchReports();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(mailFailureText(detail || 'Renvoi impossible'), { duration: 12000 });
      fetchReports();
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const euro = /^\d{2}\.\d{2}\.\d{4}$/;
      const response = await desktopApi.getReports({
        limit: reportPageSize,
        offset: reportPage * reportPageSize,
        agent_name: appliedFilters.agent || undefined,
        date_from: euro.test(appliedFilters.from) ? appliedFilters.from : undefined,
        date_to: euro.test(appliedFilters.to) ? appliedFilters.to : undefined,
        adminToken,
        withTotal: true,
      });
      const items = Array.isArray(response) ? response : (response.items || []);
      setReports(items);
      setReportTotal(Array.isArray(response) ? items.length : Number(response.total || 0));
    } catch (error) {
      toast.error('Erreur lors du chargement des rapports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && adminToken) fetchReports();
  }, [isAuthenticated, adminToken, reportPage, appliedFilters]);

  const fetchStatistics = async () => {
    try {
      const response = await desktopApi.getAdminStatistics(adminToken);
      setStatistics(response);
    } catch (error) {
      console.error('Erreur stats:', error);
    }
  };

  const fetchAlertSettings = async () => {
    try {
      const [titleRes, msgRes] = await Promise.all([
        desktopApi.getSetting('alert_title', adminToken).then((data) => ({ data })).catch(() => null),
        desktopApi.getSetting('alert_message', adminToken).then((data) => ({ data })).catch(() => null),
      ]);
      if (titleRes?.data?.value) setAlertTitle(titleRes.data.value);
      if (msgRes?.data?.value) setAlertMessage(msgRes.data.value);
    } catch (e) { /* keep defaults */ }
  };

  const fetchEstablishmentSettings = async () => {
    try {
      const [nameRes, subtitleRes, startRes, endRes, logoRes, heroRes] = await Promise.all([
        desktopApi.getSetting('establishment_name', adminToken).catch(() => null),
        desktopApi.getSetting('establishment_subtitle', adminToken).catch(() => null),
        desktopApi.getSetting('default_shift_start', adminToken).catch(() => null),
        desktopApi.getSetting('default_shift_end', adminToken).catch(() => null),
        desktopApi.getSetting('logo_path', adminToken).catch(() => null),
        desktopApi.getSetting('hero_image_path', adminToken).catch(() => null),
      ]);
      if (nameRes?.value) setEstablishmentName(nameRes.value);
      if (subtitleRes?.value) setEstablishmentSubtitle(subtitleRes.value);
      if (startRes?.value) setDefaultShiftStart(startRes.value);
      if (endRes?.value) setDefaultShiftEnd(endRes.value);
      if (logoRes?.value) setLogoPath(logoRes.value);
      if (heroRes?.value) setHeroImagePath(heroRes.value);
    } catch (e) { /* keep current values */ }
  };

  const fetchReportConfig = async () => {
    try {
      const res = await desktopApi.getSetting('report_config', adminToken).catch(() => null);
      if (res?.value) setReportConfig(JSON.parse(res.value));
    } catch (e) { /* keep null */ }
  };

  const persistReportConfig = async (next) => {
    setReportConfig(next);
    try {
      await desktopApi.updateSetting('report_config', JSON.stringify(next), adminToken);
      toast.success('Configuration des zones enregistrée');
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde des zones');
    }
  };

  const zoneList = (zoneKey, field) => (reportConfig?.[zoneKey]?.[field]) || [];

  const addZoneItem = (zoneKey, field, value) => {
    const v = (value || '').trim();
    if (!v) return;
    const current = reportConfig || {};
    const zone = current[zoneKey] || {};
    const list = zone[field] || [];
    if (list.includes(v)) { toast.error('Déjà présent'); return; }
    persistReportConfig({ ...current, [zoneKey]: { ...zone, [field]: [...list, v] } });
  };

  const removeZoneItem = (zoneKey, field, value) => {
    const current = reportConfig || {};
    const zone = current[zoneKey] || {};
    const list = (zone[field] || []).filter(x => x !== value);
    persistReportConfig({ ...current, [zoneKey]: { ...zone, [field]: list } });
  };

  const moveZoneItem = (zoneKey, field, index, delta) => {
    const current = reportConfig || {};
    const zone = current[zoneKey] || {};
    const list = [...(zone[field] || [])];
    const target = index + delta;
    if (index < 0 || target < 0 || target >= list.length) return;
    [list[index], list[target]] = [list[target], list[index]];
    persistReportConfig({ ...current, [zoneKey]: { ...zone, [field]: list } });
  };

  const renderOrderedList = (zoneKey, field) => {
    const list = zoneList(zoneKey, field);
    if (list.length === 0) {
      return <span className="text-xs text-muted-foreground">Liste vide</span>;
    }
    return (
      <div className="space-y-1.5 my-2">
        {list.map((item, index) => (
          <div key={`${field}-${item}`} className="flex items-center gap-2 bg-zinc-800 px-2 py-1.5 rounded text-xs">
            <span className="text-muted-foreground w-5 shrink-0 tabular-nums">{index + 1}.</span>
            <span className="flex-1 min-w-0 break-words">{item}</span>
            <div className="flex items-center gap-0.5 shrink-0">
              <button
                type="button"
                title="Monter"
                disabled={index === 0}
                onClick={() => moveZoneItem(zoneKey, field, index, -1)}
                className="p-1 rounded hover:bg-zinc-700 disabled:opacity-30 disabled:pointer-events-none"
              >
                <ArrowUp className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                title="Descendre"
                disabled={index === list.length - 1}
                onClick={() => moveZoneItem(zoneKey, field, index, 1)}
                className="p-1 rounded hover:bg-zinc-700 disabled:opacity-30 disabled:pointer-events-none"
              >
                <ArrowDown className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                title="Supprimer"
                onClick={() => removeZoneItem(zoneKey, field, item)}
                className="p-1 rounded text-destructive hover:bg-zinc-700"
              >
                ✕
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const fetchRemarkTemplates = async () => {
    try {
      const res = await desktopApi.getSetting('remark_templates', adminToken).catch(() => null);
      if (res?.value) setRemarkTemplates(JSON.parse(res.value));
    } catch (e) { /* keep null */ }
  };

  const persistRemarkTemplates = async (next) => {
    setRemarkTemplates(next);
    try {
      await desktopApi.updateSetting('remark_templates', JSON.stringify(next), adminToken);
      toast.success('Remarques fréquentes enregistrées');
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde des remarques');
    }
  };

  const addRemarkTemplate = (category, value) => {
    const v = (value || '').trim();
    if (!v) return;
    const current = remarkTemplates || {};
    const list = current[category] || [];
    if (list.includes(v)) { toast.error('Déjà présent'); return; }
    persistRemarkTemplates({ ...current, [category]: [...list, v] });
  };

  const removeRemarkTemplate = (category, value) => {
    const current = remarkTemplates || {};
    persistRemarkTemplates({
      ...current,
      [category]: (current[category] || []).filter(x => x !== value),
    });
  };

  const moveRemarkTemplate = (category, index, delta) => {
    const current = remarkTemplates || {};
    const list = [...(current[category] || [])];
    const target = index + delta;
    if (index < 0 || target < 0 || target >= list.length) return;
    [list[index], list[target]] = [list[target], list[index]];
    persistRemarkTemplates({ ...current, [category]: list });
  };

  const addRemarkCategory = (name) => {
    const v = (name || '').trim();
    if (!v) return;
    const current = remarkTemplates || {};
    if (current[v]) { toast.error('Catégorie déjà présente'); return; }
    persistRemarkTemplates({ ...current, [v]: [] });
  };

  const removeRemarkCategory = (category) => {
    const { [category]: _removed, ...rest } = remarkTemplates || {};
    persistRemarkTemplates(rest);
  };

  const fetchAppInfo = async () => {
    try {
      setAppInfo(await desktopApi.getAppInfo());
    } catch (e) { /* keep empty */ }
  };

  const fetchAuditLog = async () => {
    if (!adminToken) return;
    try {
      const data = await desktopApi.getAuditLog(adminToken, 100);
      setAuditLog(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur audit:', error);
    }
  };

  const handleSaveAlert = async () => {
    try {
      await Promise.all([
        desktopApi.updateSetting('alert_title', alertTitle, adminToken),
        desktopApi.updateSetting('alert_message', alertMessage, adminToken),
      ]);
      toast.success('Message d\'alerte mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleSaveEstablishment = async () => {
    try {
      await Promise.all([
        desktopApi.updateSetting('establishment_name', establishmentName, adminToken),
        desktopApi.updateSetting('establishment_subtitle', establishmentSubtitle, adminToken),
        desktopApi.updateSetting('default_shift_start', defaultShiftStart, adminToken),
        desktopApi.updateSetting('default_shift_end', defaultShiftEnd, adminToken),
        desktopApi.updateSetting('logo_path', logoPath, adminToken),
        desktopApi.updateSetting('hero_image_path', heroImagePath, adminToken),
      ]);
      toast.success('Paramètres établissement sauvegardés');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde des paramètres');
    }
  };

  const fetchReceptionistes = async () => {
    try {
      const res = await desktopApi.getSetting('receptionistes', adminToken).then((data) => ({ data })).catch(() => null);
      if (res?.data?.value) {
        try { setReceptionistes(JSON.parse(res.data.value)); } catch (e) { /* */ }
      }
    } catch (e) { /* */ }
  };

  const handleAddReceptioniste = async () => {
    if (!newReceptioniste.trim()) { toast.error('Veuillez entrer un nom'); return; }
    const updated = [...receptionistes, newReceptioniste.trim()];
    try {
      await desktopApi.updateSetting('receptionistes', JSON.stringify(updated), adminToken);
      setReceptionistes(updated);
      setNewReceptioniste('');
      toast.success('Réceptionniste ajouté(e)');
    } catch (error) { toast.error('Erreur'); }
  };

  const handleDeleteReceptioniste = async (name) => {
    const updated = receptionistes.filter(r => r !== name);
    try {
      await desktopApi.updateSetting('receptionistes', JSON.stringify(updated), adminToken);
      setReceptionistes(updated);
      toast.success('Réceptionniste supprimé(e)');
    } catch (error) { toast.error('Erreur'); }
  };

  const handleAddAgent = async () => {
    if (!newAgentName.trim()) {
      toast.error('Veuillez entrer un nom');
      return;
    }
    try {
      await desktopApi.createAgent(newAgentName, adminToken);
      toast.success('Agent ajouté');
      setNewAgentName('');
      fetchAgents();
      fetchStatistics();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const credentialDraft = (agent) => credentialDrafts[agent.id] || {
    username: agent.username || '',
    password: '',
    mustChange: !agent.has_password,
  };

  const patchCredential = (agentId, patch) => {
    setCredentialDrafts((prev) => {
      const agent = agents.find((item) => item.id === agentId);
      const base = prev[agentId] || {
        username: agent?.username || '',
        password: '',
        mustChange: !agent?.has_password,
      };
      return { ...prev, [agentId]: { ...base, ...patch } };
    });
  };

  const handleSetAgentPassword = async (agent) => {
    const draft = credentialDraft(agent);
    if (!draft.username.trim() || !draft.password) {
      toast.error('Identifiant et mot de passe requis');
      return;
    }
    try {
      await desktopApi.setAgentPassword(agent.id, {
        username: draft.username.trim(),
        password: draft.password,
        must_change_password: !!draft.mustChange,
      }, adminToken);
      toast.success('Mot de passe agent enregistré');
      setCredentialDrafts((prev) => ({ ...prev, [agent.id]: { ...draft, password: '' } }));
      fetchAgents();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Impossible d\'enregistrer le mot de passe');
    }
  };

  const handleUnlockAgent = async (agentId) => {
    try {
      await desktopApi.unlockAgent(agentId, adminToken);
      toast.success('Compte déverrouillé');
      fetchAgents();
    } catch (error) {
      toast.error('Impossible de déverrouiller le compte');
    }
  };

  const fetchLoginAudit = async () => {
    if (!adminToken) return;
    try {
      const data = await desktopApi.getLoginAudit(adminToken, 100);
      setLoginAudit(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur journal de connexion:', error);
    }
  };

  const handleAgentPresence = async (action, agent, success) => {
    try {
      await action(agent.id, adminToken);
      toast.success(success);
      fetchAgents();
      fetchStatistics();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Action impossible');
    }
  };

  const handleDeleteAgent = async (agentId, agentName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${agentName} ?`)) {
      return;
    }
    try {
      await desktopApi.deleteAgent(agentId, adminToken);
      toast.success('Agent supprimé');
      fetchAgents();
      fetchStatistics();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur lors de la suppression');
    }
  };

  const handleAccountFlag = async (agent, kind, enabled) => {
    try {
      if (kind === 'admin') {
        await desktopApi.setAdminRights(agent.id, enabled, adminToken);
      } else {
        await desktopApi.setAgentAccess(agent.id, enabled, adminToken);
      }
      toast.success('Compte mis à jour');
      fetchAgents();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Action impossible');
      fetchAgents();
    }
  };

  const handleAddManoeuvre = async () => {
    if (!newManoeuvreName.trim()) {
      toast.error('Veuillez entrer un nom de manœuvre');
      return;
    }
    try {
      await desktopApi.createManoeuvre(newManoeuvreName, adminToken);
      toast.success('Manœuvre ajoutée');
      setNewManoeuvreName('');
      fetchManoeuvres();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleDeleteManoeuvre = async (manoeuvreId, manoeuvreName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${manoeuvreName}" ?`)) {
      return;
    }
    try {
      await desktopApi.deleteManoeuvre(manoeuvreId, adminToken);
      toast.success('Manœuvre supprimée');
      fetchManoeuvres();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteReport = async (reportId, reportDate) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le rapport du ${reportDate} ? Cette action est irréversible.`)) {
      return;
    }
    try {
      await desktopApi.deleteReport(reportId, adminToken);
      toast.success('Rapport supprimé');
      fetchReports();
      fetchStatistics();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleOpenReport = async (reportId) => {
    try {
      const html = await desktopApi.getReportHtml(reportId, adminToken);
      const url = window.URL.createObjectURL(new Blob([html], { type: 'text/html' }));
      window.open(url, '_blank', 'noopener');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      toast.error('Rapport illisible');
    }
  };

  const handleDownloadPdf = async (reportId) => {
    try {
      const { blob, filename } = await desktopApi.getReportDocument(reportId, adminToken);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || `rapport_${reportId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
      toast.success('Rapport téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleExportCsv = () => {
    validateBeforeExport().then((ok) => {
      if (!ok) return;
      runExportCsv();
    });
  };

  const validateBeforeExport = async () => {
    try {
      const result = await desktopApi.validateReports(adminToken);
      setValidationResult(result);
      if (result?.count > 0) {
        return window.confirm(`${result.count} rapport(s) ont des champs importants manquants. Continuer l'export quand même ?`);
      }
      return true;
    } catch (error) {
      return window.confirm("La validation avant export a échoué. Continuer l'export quand même ?");
    }
  };

  const runExportCsv = () => {
    let url = `${API}/admin/reports/export`;
    const params = [];
    if (filterDateFrom) params.push(`date_from=${filterDateFrom}`);
    if (filterDateTo) params.push(`date_to=${filterDateTo}`);
    if (filterAgent) params.push(`agent_name=${encodeURIComponent(filterAgent)}`);
    if (params.length > 0) url += '?' + params.join('&');

    if (desktopApi.isDesktopBridge()) {
      desktopApi.exportCsvSaveAs(adminToken, {
        date_from: filterDateFrom || null,
        date_to: filterDateTo || null,
        agent_name: filterAgent || null,
      }).then((result) => {
        if (result?.cancelled) return;
        if (result?.success) {
          toast.success(`CSV exporté: ${result.path}`);
          return;
        }
        toast.error("Erreur lors de l'export CSV");
      }).catch(() => {
        toast.error("Erreur lors de l'export CSV");
      });
      return;
    }

    axios.get(url, {
      headers: { 'X-Admin-Token': adminToken },
      responseType: 'blob',
    }).then((res) => {
      const blob = new Blob([res.data], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = 'rapports_vatel.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export CSV téléchargé');
    }).catch(() => {
      toast.error("Erreur lors de l'export CSV");
    });
  };

  const handleValidateReports = async () => {
    try {
      const result = await desktopApi.validateReports(adminToken);
      setValidationResult(result);
      if (result?.count > 0) {
        toast.warning(`${result.count} rapport(s) avec champs importants manquants`);
      } else {
        toast.success('Aucun problème important détecté');
      }
    } catch (error) {
      toast.error('Erreur lors de la validation');
    }
  };

  const handleExportMonthlyZip = async () => {
    if (!zipMonth) {
      toast.error('Veuillez choisir un mois');
      return;
    }
    const validationOk = await validateBeforeExport();
    if (!validationOk) return;
    try {
      const result = await desktopApi.exportMonthlyZip(adminToken, zipMonth);
      if (result?.cancelled) return;
      if (result?.success && result.path) {
        toast.success(`ZIP exporté: ${result.path}`);
        return;
      }
      if (result?.success && result.blob) {
        const blob = new Blob([result.blob], { type: 'application/zip' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = result.filename || `vatel_export_${zipMonth}.zip`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('ZIP mensuel téléchargé');
        return;
      }
      toast.error("Erreur lors de l'export ZIP");
    } catch (error) {
      toast.error("Erreur lors de l'export ZIP");
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Les nouveaux mots de passe ne correspondent pas');
      return;
    }
    if (newPassword.length < 12) {
      toast.error('Le mot de passe doit faire au moins 12 caractères, avec majuscule, minuscule, chiffre et caractère spécial');
      return;
    }
    
    try {
      await desktopApi.changeAdminPassword(currentPassword, newPassword, adminToken);
      toast.success('Mot de passe modifié avec succès');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      if (error.response?.status === 401) {
        const detail = error.response?.data?.detail;
        toast.error(detail === 'Session expirée' ? 'Session expirée' : 'Mot de passe actuel incorrect');
      } else {
        toast.error('Erreur lors du changement de mot de passe');
      }
    }
  };

  const handleExportDb = () => {
    if (desktopApi.isDesktopBridge()) {
      desktopApi.exportDbSaveAs(adminToken).then((result) => {
        if (result?.cancelled) {
          return;
        }
        if (result?.success) {
          toast.success(`Base exportée: ${result.path}`);
          return;
        }
        toast.error("Erreur lors de l'export de la base");
      }).catch(() => {
        toast.error("Erreur lors de l'export de la base");
      });
      return;
    }

    axios.get(`${API}/admin/db/export`, {
      headers: { 'X-Admin-Token': adminToken },
      responseType: 'blob',
    }).then((res) => {
      const blob = new Blob([res.data], { type: 'application/octet-stream' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = 'vatel.sql';
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export de la base téléchargé');
    }).catch(() => {
      toast.error("Erreur lors de l'export de la base");
    });
  };

  // Filtrer les rapports
  const filteredReports = reports;
  const reportPageCount = Math.max(1, Math.ceil(reportTotal / reportPageSize));

  if (!isAuthenticated) {
    return (
      <Dialog open={showPasswordDialog} onOpenChange={(open) => {
        if (!open) navigate('/dashboard');
      }}>
        <DialogContent className="bg-card border border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <Lock className="w-6 h-6 text-primary" />
              Accès Administrateur
            </DialogTitle>
            <DialogDescription>
              Identifiant et mot de passe du compte administrateur. L'identifiant peut rester vide s'il n'y a qu'un seul compte.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label htmlFor="admin-username">Identifiant</Label>
              <Input
                id="admin-username"
                type="text"
                autoComplete="username"
                value={adminUsername}
                onChange={(e) => setAdminUsername(e.target.value)}
                data-testid="input-admin-username"
                placeholder="ex. luca.l"
              />
            </div>
            <div>
              <Label htmlFor="admin-password">Mot de passe</Label>
              <Input
                id="admin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                data-testid="input-admin-password"
                placeholder="Entrez le mot de passe"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex-1 h-11 px-4 rounded-md bg-secondary hover:bg-secondary/80 text-secondary-foreground font-medium transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handlePasswordSubmit}
                data-testid="btn-submit-password"
                className="flex-1 h-11 px-4 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground font-semibold transition-colors"
              >
                Valider
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
    <Dialog open={showMigrationDialog} onOpenChange={setShowMigrationDialog}>
      <DialogContent className="bg-card border border-border">
        <DialogHeader>
          <DialogTitle>Assistant migration base de données</DialogTitle>
          <DialogDescription>
            La base est maintenant stockée dans APPDATA (profil utilisateur Windows).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm text-muted-foreground">
          <p>Nouvelle DB: <code>{migrationStatus?.db_path || 'N/A'}</code></p>
          {migrationStatus?.legacyFound && (
            <p>Ancienne DB détectée: <code>{migrationStatus?.legacy_path}</code></p>
          )}
          {migrationStatus?.migrated && (
            <p>Migration automatique effectuée avec succès.</p>
          )}
          {migrationStatus?.backupPath && (
            <p>Backup ancien fichier: <code>{migrationStatus.backupPath}</code></p>
          )}
          {migrationStatus?.error && (
            <p className="text-destructive">Erreur migration: {migrationStatus.error}</p>
          )}
          <p>La base est sur le serveur. Utilisez Exporter la base pour en télécharger une copie.</p>
        </div>
      </DialogContent>
    </Dialog>
    <div className="min-h-screen bg-background pt-[calc(1rem+var(--sat))] pl-[calc(1rem+var(--sal))] pr-[calc(1rem+var(--sar))] pb-[calc(10.5rem+env(safe-area-inset-bottom,0px))] md:pt-[calc(2.5rem+var(--sat))] md:pl-[calc(2.5rem+var(--sal))] md:pr-[calc(2.5rem+var(--sar))] md:pb-20 overflow-x-hidden">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            data-testid="back-button"
            className="flex min-h-11 items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </button>
          <h1 className="text-4xl md:text-5xl tracking-tight uppercase font-bold mb-2">
            Administration
          </h1>
          <p className="text-muted-foreground">Gestion complète du système</p>
        </div>

        {/* Statistiques */}
        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <FileText className="w-4 h-4" />
                <span className="text-xs uppercase">Rapports</span>
              </div>
              <p className="text-3xl font-bold text-primary">{statistics.total_reports}</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <User className="w-4 h-4" />
                <span className="text-xs uppercase">Agents</span>
              </div>
              <p className="text-3xl font-bold text-primary">{statistics.total_agents}</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4 col-span-2">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <BarChart3 className="w-4 h-4" />
                <span className="text-xs uppercase">Rapports par agent</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(statistics.reports_by_agent).map(([agent, count]) => (
                  <span key={agent} className="text-xs bg-zinc-800 px-2 py-1 rounded">
                    {agent}: <strong>{count}</strong>
                  </span>
                ))}
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4 col-span-2" data-testid="stats-by-month">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Calendar className="w-4 h-4" />
                <span className="text-xs uppercase">Rapports par mois</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {Object.keys(statistics.reports_by_month || {}).length === 0 ? (
                  <span className="text-xs text-muted-foreground">Aucun rapport daté</span>
                ) : Object.entries(statistics.reports_by_month).map(([month, count]) => (
                  <span key={month} className="text-xs bg-zinc-800 px-2 py-1 rounded">
                    {month}: <strong>{count}</strong>
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        <Accordion type="multiple" value={openSections} onValueChange={setOpenSections} className="space-y-4">
          {/* Gestion des Agents */}
          <AccordionItem value="agents" className="bg-card border border-border rounded-lg">
            <AccordionTrigger className="px-6 py-4 hover:no-underline">
              <div className="flex items-center gap-3">
                <ShieldCheck className="w-5 h-5 text-primary" />
                <span className="text-xl font-bold uppercase">Utilisateurs</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-4">
              <div className="flex gap-3 mb-4">
                <Input
                  type="text"
                  placeholder="Nom de l'agent"
                  aria-label="Nom du nouvel agent"
                  value={newAgentName}
                  onChange={(e) => setNewAgentName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddAgent()}
                  data-testid="input-new-agent"
                  className="flex-1"
                />
                <button
                  onClick={handleAddAgent}
                  data-testid="btn-add-agent"
                  className="h-11 px-6 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground font-semibold transition-all"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              {authMode === 'password' && (
                <p className="text-sm text-muted-foreground mb-3">
                  Identifiant et mot de passe du téléphone : 12 caractères, majuscule, minuscule, chiffre et caractère spécial.
                  Cochez « changer à la première connexion » pour un mot de passe provisoire.
                </p>
              )}
              <div className="space-y-2">
                {agents.map((agent) => {
                  const draft = credentialDraft(agent);
                  return (
                  <div
                    key={agent.id}
                    className="flex flex-col gap-3 p-3 bg-zinc-900/50 rounded-lg border border-border"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <ShieldCheck className="w-4 h-4 text-primary shrink-0" />
                      <span className="font-medium truncate">{agent.name}</span>
                      {agent.is_admin && <span className="text-xs uppercase tracking-wide text-primary">Admin</span>}
                      {agent.is_agent && <span className="text-xs uppercase tracking-wide text-muted-foreground">Agent</span>}
                      {accountId && agent.id === accountId && <span className="text-xs text-muted-foreground">Vous</span>}
                      {authMode === 'password' && agent.has_password && <span className="text-xs text-emerald-400">Mot de passe défini</span>}
                      {authMode === 'password' && agent.locked && <span className="text-xs text-amber-300">Verrouillé</span>}
                      {authMode === 'password' && agent.must_change_password && <span className="text-xs text-muted-foreground">Doit changer</span>}
                      {!agent.active && !agent.archived && <span className="text-xs text-amber-300">Désactivé</span>}
                      {agent.archived && <span className="text-xs text-muted-foreground">Archivé</span>}
                    </div>
                    {authMode === 'password' && (
                      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                        <Input
                          type="text"
                          autoComplete="off"
                          placeholder="Identifiant"
                          value={draft.username}
                          onChange={(e) => patchCredential(agent.id, { username: e.target.value })}
                          className="h-11"
                          data-testid={`input-agent-username-${agent.id}`}
                          aria-label={`Identifiant de ${agent.name}`}
                        />
                        <Input
                          type="password"
                          autoComplete="new-password"
                          placeholder="Nouveau mot de passe"
                          value={draft.password}
                          onChange={(e) => patchCredential(agent.id, { password: e.target.value })}
                          className="h-11"
                          data-testid={`input-agent-password-${agent.id}`}
                          aria-label={`Mot de passe de ${agent.name}`}
                        />
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleAccountFlag(agent, 'admin', !agent.is_admin)}
                        className="h-11 px-3 rounded-md bg-secondary text-sm font-semibold"
                        data-testid={`btn-admin-rights-${agent.id}`}
                      >
                        {agent.is_admin ? 'Retirer les droits admin' : 'Donner les droits admin'}
                      </button>
                      <label className="flex items-center gap-2 text-sm text-muted-foreground h-11">
                        <input
                          type="checkbox"
                          checked={!!agent.is_agent}
                          aria-label={`Accès agent de ${agent.name}`}
                          data-testid={`chk-agent-access-${agent.id}`}
                          onChange={(e) => handleAccountFlag(agent, 'agent', e.target.checked)}
                        />
                        Accès agent
                      </label>
                      {authMode === 'password' && (
                        <>
                          <label className="flex items-center gap-2 text-sm text-muted-foreground">
                            <input
                              type="checkbox"
                              checked={!!draft.mustChange}
                              onChange={(e) => patchCredential(agent.id, { mustChange: e.target.checked })}
                            />
                            Changer à la première connexion
                          </label>
                          <button
                            type="button"
                            onClick={() => handleSetAgentPassword(agent)}
                            className="h-11 px-3 rounded-md bg-primary text-primary-foreground text-sm font-semibold"
                          >
                            Enregistrer
                          </button>
                          {agent.locked && (
                            <button
                              type="button"
                              onClick={() => handleUnlockAgent(agent.id)}
                              className="h-11 px-3 rounded-md bg-secondary text-sm font-semibold"
                            >
                              Déverrouiller
                            </button>
                          )}
                        </>
                      )}
                      {agent.active && (
                        <button
                          type="button"
                          onClick={() => handleAgentPresence(desktopApi.revokeAgentSessions, agent, 'Toutes les sessions sont closes')}
                          className="h-11 px-3 rounded-md bg-secondary text-sm font-semibold"
                        >
                          Déconnecter partout
                        </button>
                      )}
                      {agent.active && (
                        <button
                          type="button"
                          onClick={() => handleAgentPresence(desktopApi.deactivateAgent, agent, 'Agent désactivé')}
                          className="h-11 px-3 rounded-md bg-secondary text-sm font-semibold"
                        >
                          Désactiver
                        </button>
                      )}
                      {!agent.archived && (
                        <button
                          type="button"
                          onClick={() => handleAgentPresence(desktopApi.archiveAgent, agent, 'Agent archivé, rapports conservés')}
                          className="h-11 px-3 rounded-md bg-secondary text-sm font-semibold"
                        >
                          Archiver
                        </button>
                      )}
                      {!agent.active && (
                        <button
                          type="button"
                          onClick={() => handleAgentPresence(desktopApi.reactivateAgent, agent, 'Agent réactivé')}
                          className="h-11 px-3 rounded-md bg-secondary text-sm font-semibold"
                        >
                          Réactiver
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteAgent(agent.id, agent.name)}
                        className="flex items-center gap-1 h-11 px-3 rounded-md bg-destructive hover:bg-destructive/90 text-destructive-foreground text-xs font-medium"
                      >
                        <Trash2 className="w-3 h-3" />
                        Supprimer
                      </button>
                    </div>
                  </div>
                  );
                })}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Gestion des Réceptionnistes */}
          <AccordionItem value="receptionistes" className="bg-card border border-border rounded-lg">
            <AccordionTrigger className="px-6 py-4 hover:no-underline">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-primary" />
                <span className="text-xl font-bold uppercase">Réceptionnistes</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-4">
              <p className="text-sm text-muted-foreground mb-4">
                Ces noms apparaîtront dans la sélection du formulaire de rapport.
              </p>
              <div className="flex gap-3 mb-4">
                <Input
                  type="text"
                  placeholder="Nom du réceptionniste"
                  value={newReceptioniste}
                  onChange={(e) => setNewReceptioniste(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddReceptioniste()}
                  data-testid="input-new-receptioniste"
                  className="flex-1"
                />
                <button
                  onClick={handleAddReceptioniste}
                  data-testid="btn-add-receptioniste"
                  className="h-11 px-6 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-2">
                {receptionistes.map((name) => (
                  <div key={name} className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-lg border border-border">
                    <div className="flex items-center gap-3">
                      <User className="w-4 h-4 text-primary" />
                      <span className="font-medium">{name}</span>
                    </div>
                    <button
                      onClick={() => handleDeleteReceptioniste(name)}
                      className="flex items-center gap-1 h-8 px-3 rounded-md bg-destructive hover:bg-destructive/90 text-destructive-foreground text-xs font-medium"
                    >
                      <Trash2 className="w-3 h-3" />
                      Supprimer
                    </button>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Gestion des Manœuvres */}
          <AccordionItem value="manoeuvres" className="bg-card border border-border rounded-lg">
            <AccordionTrigger className="px-6 py-4 hover:no-underline">
              <div className="flex items-center gap-3">
                <ClipboardList className="w-5 h-5 text-primary" />
                <span className="text-xl font-bold uppercase">Manœuvres / Tâches de Nuit</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-4">
              <p className="text-sm text-muted-foreground mb-4">
                Ces options apparaîtront dans le formulaire de rapport.
              </p>
              <div className="flex gap-3 mb-4">
                <Input
                  type="text"
                  placeholder="Nouvelle manœuvre (ex: Vérifier les extincteurs)"
                  value={newManoeuvreName}
                  onChange={(e) => setNewManoeuvreName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddManoeuvre()}
                  data-testid="input-new-manoeuvre"
                  className="flex-1"
                />
                <button
                  onClick={handleAddManoeuvre}
                  className="h-11 px-6 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-2">
                {manoeuvres.map((manoeuvre) => (
                  <div
                    key={manoeuvre.id}
                    className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-lg border border-border"
                  >
                    <div className="flex items-center gap-3">
                      <ClipboardList className="w-4 h-4 text-primary" />
                      <span className="text-sm font-medium">{manoeuvre.name}</span>
                    </div>
                    <button
                      onClick={() => handleDeleteManoeuvre(manoeuvre.id, manoeuvre.name)}
                      className="flex items-center gap-1 h-8 px-3 rounded-md bg-destructive hover:bg-destructive/90 text-destructive-foreground text-xs font-medium"
                    >
                      <Trash2 className="w-3 h-3" />
                      Supprimer
                    </button>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="mail" className="bg-card border border-border rounded-lg" data-testid="mail-section">
            <AccordionTrigger className="px-6 py-4 hover:no-underline">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-primary" />
                <span className="text-lg sm:text-xl font-bold uppercase text-left">Paramètres d'envoi</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-4">
              <p className="text-sm text-muted-foreground mb-3">
                Envoi des e-mails. L'enregistrement d'un rapport n'envoie rien.
                Le bouton « Envoyer par e-mail » n'apparaît chez l'agent que si l'envoi est activé.
                Le PDF reste toujours téléchargeable.
              </p>
              {mailConfig?.notice ? (
                <p className="text-sm text-amber-200 bg-amber-950/40 border border-amber-700/50 rounded-md p-3 mb-4" data-testid="mail-notice">
                  {mailConfig.notice}
                </p>
              ) : null}
              <p className="text-xs text-muted-foreground mb-4" data-testid="mail-help">{mailConfig?.help}</p>
              {mailForm ? (
                <>
                  <h3 className="text-sm font-bold uppercase tracking-wide mb-3">Réglages d'envoi</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                    <div className="sm:col-span-2">
                      <Label htmlFor="mail-from-name">Nom d'expéditeur affiché</Label>
                      <Input
                        id="mail-from-name"
                        value={mailForm.from_name}
                        onChange={(e) => patchMailForm('from_name', e.target.value)}
                        onFocus={(e) => { mailFocus.current = e.target; }}
                        data-mail-field="from_name"
                        data-testid="input-mail-from-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="mail-from-address">Adresse d'expéditeur</Label>
                      <Input
                        id="mail-from-address"
                        type="email"
                        value={mailForm.from_address}
                        onChange={(e) => patchMailForm('from_address', e.target.value)}
                        data-testid="input-mail-from-address"
                        autoComplete="off"
                      />
                    </div>
                    <div>
                      <Label htmlFor="mail-user">Identifiant SMTP</Label>
                      <Input
                        id="mail-user"
                        value={mailForm.user}
                        onChange={(e) => patchMailForm('user', e.target.value)}
                        data-testid="input-mail-user"
                        autoComplete="off"
                      />
                    </div>
                    <div>
                      <Label htmlFor="mail-host">Serveur SMTP</Label>
                      <Input
                        id="mail-host"
                        value={mailForm.host}
                        onChange={(e) => patchMailForm('host', e.target.value)}
                        data-testid="input-mail-host"
                        autoComplete="off"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="mail-port">Port</Label>
                        <Input
                          id="mail-port"
                          type="number"
                          min="1"
                          max="65535"
                          value={mailForm.port}
                          onChange={(e) => patchMailForm('port', e.target.value)}
                          data-testid="input-mail-port"
                        />
                      </div>
                      <div>
                        <Label htmlFor="mail-security">Sécurité</Label>
                        <select
                          id="mail-security"
                          value={mailForm.security}
                          onChange={(e) => patchMailForm('security', e.target.value)}
                          data-testid="select-mail-security"
                          className="w-full h-11 px-3 rounded-md border border-input bg-transparent text-base min-[1025px]:text-sm"
                        >
                          <option value="starttls">STARTTLS</option>
                          <option value="ssl">SSL</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="mail-reply-to">Reply-To</Label>
                      <Input
                        id="mail-reply-to"
                        type="email"
                        value={mailForm.reply_to}
                        onChange={(e) => patchMailForm('reply_to', e.target.value)}
                        data-testid="input-mail-reply-to"
                        placeholder="facultatif"
                        autoComplete="off"
                      />
                    </div>
                    <label className="flex items-center gap-3 h-11 mt-5 text-sm font-medium">
                      <input
                        type="checkbox"
                        checked={!!mailForm.enabled}
                        onChange={(e) => patchMailForm('enabled', e.target.checked)}
                        data-testid="input-mail-enabled"
                        className="h-4 w-4"
                      />
                      Envoi activé
                    </label>
                    <div className="sm:col-span-2">
                      <Label>Mot de passe SMTP</Label>
                      <p className="text-sm mb-2" data-testid="mail-password-status">
                        {mailConfig?.password_set ? 'Défini' : 'Non défini'}
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          setReplacePassword((current) => !current);
                          setMailPassword('');
                        }}
                        data-testid="btn-replace-password"
                        className="h-11 px-4 rounded-md bg-secondary text-secondary-foreground text-sm font-semibold"
                      >
                        Remplacer le mot de passe
                      </button>
                      {replacePassword ? (
                        <Input
                          type="password"
                          value={mailPassword}
                          onChange={(e) => setMailPassword(e.target.value)}
                          data-testid="input-mail-password"
                          autoComplete="new-password"
                          placeholder="Nouveau mot de passe"
                          className="mt-2"
                        />
                      ) : null}
                      <p className="text-xs text-muted-foreground mt-2">
                        Un champ vide conserve le mot de passe déjà enregistré. Il n'est jamais réaffiché.
                      </p>
                    </div>
                  </div>

                  <h3 className="text-sm font-bold uppercase tracking-wide mb-3">Modèles du message</h3>
                  <div className="space-y-4 mb-4">
                    <div>
                      <Label htmlFor="mail-subject">Modèle d'objet</Label>
                      <Input
                        id="mail-subject"
                        value={mailForm.subject_template}
                        onChange={(e) => patchMailForm('subject_template', e.target.value)}
                        onFocus={(e) => { mailFocus.current = e.target; }}
                        data-mail-field="subject_template"
                        data-testid="input-mail-subject"
                      />
                    </div>
                    <div>
                      <Label htmlFor="mail-filename">Modèle de nom du PDF joint</Label>
                      <Input
                        id="mail-filename"
                        value={mailForm.filename_template}
                        onChange={(e) => patchMailForm('filename_template', e.target.value)}
                        onFocus={(e) => { mailFocus.current = e.target; }}
                        data-mail-field="filename_template"
                        data-testid="input-mail-filename"
                      />
                    </div>
                    <div>
                      <Label htmlFor="mail-body">Modèle de corps</Label>
                      <textarea
                        id="mail-body"
                        value={mailForm.body_template}
                        onChange={(e) => patchMailForm('body_template', e.target.value)}
                        onFocus={(e) => { mailFocus.current = e.target; }}
                        data-mail-field="body_template"
                        data-testid="input-mail-body"
                        className="w-full min-h-[140px] px-3 py-2 rounded-md border border-input bg-transparent text-base min-[1025px]:text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <p className="text-xs uppercase text-muted-foreground mb-2">Balises</p>
                    <div className="flex flex-wrap gap-2">
                      {MAIL_TAGS.map((tag) => (
                        <button
                          key={tag.id}
                          type="button"
                          title={tag.hint}
                          onClick={() => insertMailTag(tag.id)}
                          data-testid={`btn-mail-tag-${tag.id}`}
                          className="h-11 px-3 rounded-md border border-border bg-zinc-900/50 text-sm font-medium"
                        >
                          {`{${tag.id}}`}
                        </button>
                      ))}
                    </div>
                    <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                      {MAIL_TAGS.map((tag) => (
                        <li key={tag.id}>{`{${tag.id}}`} — {tag.hint}</li>
                      ))}
                    </ul>
                  </div>
                  <button
                    type="button"
                    onClick={handleRestoreMailDefaults}
                    data-testid="btn-restore-mail-defaults"
                    className="h-11 px-4 mb-4 rounded-md bg-secondary text-secondary-foreground text-sm font-semibold"
                  >
                    Rétablir les valeurs par défaut
                  </button>
                  {(() => {
                    const sample = mailConfig?.example?.context || {
                      date_debut: '22.10.2026',
                      date_fin: '23.10.2026',
                      initiales: 'PH',
                      agent: 'Patrick Hubert',
                      date_envoi: '24.10.2026',
                    };
                    const senderName = renderMailTemplate(mailForm.from_name, sample).replace(/\s+/g, ' ').trim();
                    const senderAddr = (mailForm.from_address || mailForm.user || '').trim();
                    const fromLine = senderName && senderAddr
                      ? `${senderName} <${senderAddr}>`
                      : (senderAddr || senderName || '—');
                    const subjectLine = renderMailTemplate(mailForm.subject_template, sample);
                    const fileLine = safePreviewFilename(renderMailTemplate(mailForm.filename_template, sample));
                    const bodyLine = renderMailTemplate(mailForm.body_template, sample);
                    const unknown = ['from_name', 'subject_template', 'filename_template', 'body_template']
                      .flatMap((field) => {
                        const found = [];
                        String(mailForm[field] || '').replace(/\{([^{}]+)\}/g, (_, raw) => {
                          const name = String(raw || '').trim();
                          if (!MAIL_TAGS.some((tag) => tag.id === name) && !found.includes(name)) found.push(name);
                          return '';
                        });
                        return found;
                      });
                    return (
                      <div className="mb-4 rounded-lg border border-border bg-zinc-900/40 p-4" data-testid="mail-preview">
                        <p className="text-sm font-bold uppercase mb-1">Aperçu</p>
                        <p className="text-xs text-muted-foreground mb-3" data-testid="mail-preview-caption">
                          Exemple {sample.agent}, nuit du {sample.date_debut} au {sample.date_fin}, initiales {sample.initiales}. Calculé avant l'enregistrement.
                        </p>
                        {unknown.length ? (
                          <p className="text-sm text-amber-200 mb-3" data-testid="mail-preview-unknown">
                            Balise inconnue : {unknown.map((name) => `{${name}}`).join(', ')}. L'enregistrement sera refusé.
                          </p>
                        ) : null}
                        <p className="text-xs uppercase text-muted-foreground">Expéditeur</p>
                        <p className="text-sm mb-2 break-all" data-testid="mail-preview-from">{fromLine}</p>
                        <p className="text-xs uppercase text-muted-foreground">Objet</p>
                        <p className="text-sm mb-2 break-words" data-testid="mail-preview-subject">{subjectLine}</p>
                        <p className="text-xs uppercase text-muted-foreground">Nom du PDF</p>
                        <p className="text-sm mb-2 break-all" data-testid="mail-preview-filename">{fileLine}</p>
                        <p className="text-xs uppercase text-muted-foreground">Corps</p>
                        <pre className="whitespace-pre-wrap font-sans text-sm" data-testid="mail-preview-body">{bodyLine}</pre>
                      </div>
                    );
                  })()}
                  <button
                    type="button"
                    onClick={handleSaveMailSettings}
                    disabled={mailSaving}
                    data-testid="btn-save-mail"
                    className="h-11 w-full sm:w-auto px-6 mb-6 rounded-md bg-primary text-primary-foreground font-semibold disabled:opacity-50"
                  >
                    {mailSaving ? 'Enregistrement…' : 'Enregistrer les réglages'}
                  </button>
                </>
              ) : (
                <p className="text-sm text-muted-foreground mb-4">Chargement des réglages d'envoi…</p>
              )}

              <h3 className="text-sm font-bold uppercase tracking-wide mb-3">Liste de diffusion</h3>
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <Input
                  type="email"
                  placeholder="adresse@exemple.ch"
                  value={mailAddress}
                  onChange={(e) => setMailAddress(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddRecipient()}
                  data-testid="input-mail-recipient"
                  aria-label="Destinataire e-mail"
                  className="flex-1"
                />
                <button
                  type="button"
                  onClick={handleAddRecipient}
                  data-testid="btn-add-recipient"
                  className="h-11 px-6 rounded-md bg-primary text-primary-foreground font-semibold"
                >
                  Ajouter
                </button>
              </div>
              <div className="space-y-2 mb-6">
                {(mailConfig?.recipients || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">Aucune adresse dans la liste de diffusion.</p>
                ) : (mailConfig.recipients || []).map((recipient) => (
                  <div key={recipient.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-zinc-900/50 rounded-lg border border-border" data-testid={`mail-recipient-${recipient.address}`}>
                    {mailEditId === recipient.id ? (
                      <>
                        <Input
                          type="email"
                          value={mailEditAddress}
                          onChange={(e) => setMailEditAddress(e.target.value)}
                          data-testid="input-mail-edit-recipient"
                          className="flex-1"
                        />
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={handleUpdateRecipient}
                            data-testid="btn-save-recipient"
                            className="h-11 px-3 rounded-md bg-primary text-primary-foreground text-xs font-medium"
                          >
                            Enregistrer
                          </button>
                          <button
                            type="button"
                            onClick={() => { setMailEditId(''); setMailEditAddress(''); }}
                            className="h-11 px-3 rounded-md bg-secondary text-secondary-foreground text-xs font-medium"
                          >
                            Annuler
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <span className="text-sm break-all">{recipient.address}</span>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => { setMailEditId(recipient.id); setMailEditAddress(recipient.address); }}
                            data-testid={`btn-edit-recipient-${recipient.id}`}
                            className="h-11 px-3 rounded-md bg-secondary text-secondary-foreground text-xs font-medium"
                          >
                            Modifier
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveRecipient(recipient.id)}
                            data-testid={`btn-remove-recipient-${recipient.id}`}
                            className="h-11 px-3 rounded-md bg-destructive text-destructive-foreground text-xs font-medium"
                          >
                            Retirer
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>

              <h3 className="text-sm font-bold uppercase tracking-wide mb-3">E-mail de test</h3>
              <p className="text-xs text-muted-foreground mb-3">
                Le test utilise les modèles enregistrés et l'exemple Patrick Hubert (22.10.2026 – 23.10.2026, PH).
              </p>
              <Label htmlFor="mail-test-to">Adresse de test</Label>
              <div className="flex flex-col sm:flex-row gap-3 mb-3">
                <Input
                  id="mail-test-to"
                  type="email"
                  placeholder="adresse@exemple.ch"
                  value={mailTestTo}
                  onChange={(e) => setMailTestTo(e.target.value)}
                  data-testid="input-mail-test-to"
                  className="flex-1"
                />
                <button
                  type="button"
                  onClick={handleTestMail}
                  data-testid="btn-mail-test"
                  className="h-11 px-4 rounded-md bg-secondary text-secondary-foreground text-sm font-semibold"
                >
                  Envoyer un e-mail de test
                </button>
              </div>
              {mailTestResult ? (
                <p className="text-sm" data-testid="mail-test-result">{mailTestResult}</p>
              ) : null}
            </AccordionContent>
          </AccordionItem>

          {/* Gestion des Rapports */}
          <AccordionItem value="reports" className="bg-card border border-border rounded-lg">
            <AccordionTrigger className="px-6 py-4 hover:no-underline">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-primary" />
                <span className="text-lg sm:text-xl font-bold uppercase text-left">Gestion des rapports</span>
                <span className="text-sm text-muted-foreground">({reportTotal})</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-4">
              {/* Filtres et Export */}
              <div className="flex flex-wrap gap-3 mb-4 p-4 bg-zinc-900/50 rounded-lg">
                <div className="flex-1 min-w-[150px]">
                  <Label className="text-xs mb-1 block" htmlFor="admin-filter-agent">Filtrer par agent</Label>
                  <select
                    id="admin-filter-agent"
                    aria-label="Filtrer par agent"
                    value={filterAgent}
                    onChange={(e) => {
                      const agent = e.target.value;
                      setFilterAgent(agent);
                      setReportPage(0);
                      setAppliedFilters((prev) => ({ ...prev, agent }));
                    }}
                    className="w-full h-11 px-3 rounded-md border border-input bg-background text-base min-[1025px]:text-sm"
                  >
                    <option value="">Tous les agents</option>
                    {agents.map(agent => (
                      <option key={agent.id} value={agent.name}>{agent.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-1 min-w-[120px]">
                  <Label className="text-xs mb-1 block" htmlFor="admin-filter-from">Date début (DD.MM.YYYY)</Label>
                  <Input
                    id="admin-filter-from"
                    placeholder="01.01.2025"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                    aria-label="Date de début du filtre"
                  />
                </div>
                <div className="flex-1 min-w-[120px]">
                  <Label className="text-xs mb-1 block" htmlFor="admin-filter-to">Date fin (DD.MM.YYYY)</Label>
                  <Input
                    id="admin-filter-to"
                    placeholder="31.12.2025"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                    aria-label="Date de fin du filtre"
                  />
                </div>
                <div className="flex items-end gap-2 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={() => {
                      setReportPage(0);
                      setAppliedFilters((prev) => ({ ...prev, from: filterDateFrom.trim(), to: filterDateTo.trim() }));
                    }}
                    className="h-11 px-4 rounded-md bg-secondary text-secondary-foreground font-medium"
                  >
                    Filtrer
                  </button>
                  <button
                    onClick={handleExportCsv}
                    className="h-11 px-4 rounded-md bg-green-800 hover:bg-green-900 text-white font-medium flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Exporter CSV
                  </button>
                </div>
              </div>

              {/* Liste des rapports */}
              {loading ? (
                <p className="text-center py-8 text-muted-foreground">Chargement...</p>
              ) : filteredReports.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">Aucun rapport trouvé</p>
              ) : (
                <>
                <div className="space-y-2">
                  {filteredReports.map((report) => (
                    <div
                      key={report.id}
                      className="flex flex-col gap-3 p-3 bg-zinc-900/50 rounded-lg border border-border hover:border-primary/30 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="flex flex-col gap-1 min-w-0 sm:flex-row sm:items-center sm:gap-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
                          <span className="font-medium">{report.date_start}</span>
                        </div>
                        <div className="flex items-center gap-2 min-w-0">
                          <User className="w-4 h-4 text-muted-foreground shrink-0" />
                          <span className="text-sm text-muted-foreground truncate">{report.agent_name}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {report.shift_start} - {report.shift_end}
                        </span>
                        <span className="text-xs text-muted-foreground max-w-xs" data-testid={`mail-status-${report.id}`}>
                          {mailStatusText(report.mail)}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 w-full min-w-0 sm:flex sm:w-auto sm:items-center">
                        <button
                          type="button"
                          onClick={() => handleOpenReport(report.id)}
                          className="flex w-full sm:w-auto sm:flex-none items-center justify-center gap-1 h-11 px-3 rounded-md bg-secondary text-secondary-foreground text-xs font-medium"
                        >
                          Voir
                        </button>
                        <button
                          type="button"
                          onClick={() => handleResendMail(report.id)}
                          data-testid={`btn-resend-${report.id}`}
                          disabled={report.mail?.enabled === false}
                          className="flex w-full sm:w-auto sm:flex-none items-center justify-center gap-1 h-11 px-3 rounded-md bg-secondary text-secondary-foreground text-xs font-medium disabled:opacity-40"
                        >
                          <Mail className="w-3 h-3" />
                          Renvoyer
                        </button>
                        <button
                          onClick={() => handleDownloadPdf(report.id)}
                          className="flex w-full sm:w-auto sm:flex-none items-center justify-center gap-1 h-11 px-3 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-medium"
                        >
                          <Download className="w-3 h-3" />
                          PDF
                        </button>
                        <button
                          onClick={() => handleDeleteReport(report.id, report.date_start)}
                          className="flex w-full sm:w-auto sm:flex-none items-center justify-center gap-1 h-11 px-3 rounded-md bg-destructive hover:bg-destructive/90 text-destructive-foreground text-xs font-medium whitespace-nowrap"
                        >
                          <Trash2 className="w-3 h-3 shrink-0" />
                          Supprimer
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
                  <span className="text-sm text-muted-foreground">{reportTotal} rapport{reportTotal > 1 ? 's' : ''}</span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={reportPage === 0}
                      onClick={() => setReportPage((page) => Math.max(0, page - 1))}
                      className="h-11 px-4 rounded-md bg-secondary text-secondary-foreground text-sm font-semibold disabled:opacity-40"
                    >
                      Précédent
                    </button>
                    <span className="text-sm tabular-nums">Page {Math.min(reportPage + 1, reportPageCount)} / {reportPageCount}</span>
                    <button
                      type="button"
                      disabled={reportPage + 1 >= reportPageCount}
                      onClick={() => setReportPage((page) => page + 1)}
                      className="h-11 px-4 rounded-md bg-secondary text-secondary-foreground text-sm font-semibold disabled:opacity-40"
                    >
                      Suivant
                    </button>
                  </div>
                </div>
                </>
              )}
            </AccordionContent>
          </AccordionItem>

          {/* Message d'alerte Dashboard */}
          <AccordionItem value="alert" className="bg-card border border-border rounded-lg">
            <AccordionTrigger className="px-6 py-4 hover:no-underline">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-5 h-5 text-primary" />
                <span className="text-lg sm:text-xl font-bold uppercase text-left">Consignes (message du tableau de bord)</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-4">
              <p className="text-sm text-muted-foreground mb-4">
                Consignes affichées sur le téléphone de chaque agent. Le texte se modifie ici, y compris sur un petit écran.
              </p>
              <div className="max-w-lg space-y-4">
                <div>
                  <Label htmlFor="alert-title">Titre</Label>
                  <Input
                    id="alert-title"
                    value={alertTitle}
                    onChange={(e) => setAlertTitle(e.target.value)}
                    placeholder="Ex: Service de Nuit Actif"
                    data-testid="input-alert-title"
                  />
                </div>
                <div>
                  <Label htmlFor="alert-message">Message</Label>
                  <textarea
                    id="alert-message"
                    value={alertMessage}
                    onChange={(e) => setAlertMessage(e.target.value)}
                    placeholder="Ex: N'oubliez pas d'effectuer les rondes..."
                    data-testid="input-alert-message"
                    className="w-full min-h-[80px] px-3 py-2 rounded-md border border-input bg-transparent text-base min-[1025px]:text-sm resize-none focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
                <button
                  onClick={handleSaveAlert}
                  data-testid="btn-save-alert"
                  className="h-11 w-full sm:w-auto px-6 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                >
                  Enregistrer
                </button>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Onglet Consignes (5.0.7) : textes de référence et liste des étudiants */}
          <AccordionItem value="consignes-tab" className="bg-card border border-border rounded-lg" data-testid="admin-consignes-tab">
            <AccordionTrigger className="px-6 py-4 hover:no-underline">
              <div className="flex items-center gap-3">
                <BookOpen className="w-5 h-5 text-primary" />
                <span className="text-lg sm:text-xl font-bold uppercase text-left">Onglet Consignes (textes)</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-4">
              <ConsignesEditor adminToken={adminToken} />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="students" className="bg-card border border-border rounded-lg" data-testid="admin-students">
            <AccordionTrigger className="px-6 py-4 hover:no-underline">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-primary" />
                <span className="text-lg sm:text-xl font-bold uppercase text-left">Liste des étudiants</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-4">
              <StudentsAdmin adminToken={adminToken} />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="establishment" className="bg-card border border-border rounded-lg">
            <AccordionTrigger className="px-6 py-4 hover:no-underline">
              <div className="flex items-center gap-3">
                <ShieldCheck className="w-5 h-5 text-primary" />
                <span className="text-lg sm:text-xl font-bold uppercase text-left">Infos établissement</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
                <div>
                  <Label>Nom établissement</Label>
                  <Input value={establishmentName} onChange={(e) => setEstablishmentName(e.target.value)} placeholder="Hôtel Vatel" />
                </div>
                <div>
                  <Label>Sous-titre / Ville</Label>
                  <Input value={establishmentSubtitle} onChange={(e) => setEstablishmentSubtitle(e.target.value)} placeholder="Martigny" />
                </div>
                <div>
                  <Label>Heure début par défaut</Label>
                  <Input type="time" value={defaultShiftStart} onChange={(e) => setDefaultShiftStart(e.target.value)} />
                </div>
                <div>
                  <Label>Heure fin par défaut</Label>
                  <Input type="time" value={defaultShiftEnd} onChange={(e) => setDefaultShiftEnd(e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <Label>Logo application (menu / dashboard)</Label>
                  <Input value={logoPath} onChange={(e) => setLogoPath(e.target.value)} placeholder="/gk-logo.png" />
                </div>
                <div className="md:col-span-2">
                  <Label>Image d'accueil (sélection agent)</Label>
                  <Input value={heroImagePath} onChange={(e) => setHeroImagePath(e.target.value)} placeholder="/hotel-vatel.jpg" />
                </div>
                <div>
                  <button onClick={handleSaveEstablishment} className="h-11 w-full sm:w-auto px-6 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
                    Enregistrer les paramètres
                  </button>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="zones-editor" className="bg-card border border-border rounded-lg">
            <AccordionTrigger className="px-6 py-4 hover:no-underline">
              <div className="flex items-center gap-3">
                <ClipboardList className="w-5 h-5 text-primary" />
                <span className="text-xl font-bold uppercase">Éditeur des Zones du Rapport</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-4">
              <p className="text-sm text-muted-foreground mb-4">
                Personnalisez les salles, les points de contrôle et les options cliquables de chaque zone.
                Utilisez les flèches pour l&apos;ordre. Les salles et points Restaurant / Cuisine / Sous-sols
                sont reflétés dans le PDF en conservant le style du modèle.
              </p>
              <div className="space-y-5">
                {ZONE_DEFS.map(zone => (
                  <div key={zone.id} className="rounded-lg border border-border bg-zinc-900/40 p-4">
                    <h4 className="font-bold text-foreground mb-3 uppercase text-sm tracking-wide">{zone.label}</h4>

                    {zone.hasRooms && (
                      <div className="mb-4">
                        <Label className="text-xs uppercase text-muted-foreground">Salles</Label>
                        {renderOrderedList(zone.key, 'rooms')}
                        <div className="flex gap-2">
                          <Input
                            placeholder="Ajouter une salle (ex: 014)"
                            value={newRoomInput[zone.id] || ''}
                            onChange={(e) => setNewRoomInput(prev => ({ ...prev, [zone.id]: e.target.value }))}
                            onKeyPress={(e) => { if (e.key === 'Enter') { addZoneItem(zone.key, 'rooms', newRoomInput[zone.id]); setNewRoomInput(prev => ({ ...prev, [zone.id]: '' })); } }}
                            className="min-w-0 flex-1 h-11"
                          />
                          <button onClick={() => { addZoneItem(zone.key, 'rooms', newRoomInput[zone.id]); setNewRoomInput(prev => ({ ...prev, [zone.id]: '' })); }} className="h-9 px-3 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground text-sm"><Plus className="w-4 h-4" /></button>
                        </div>
                      </div>
                    )}

                    {zone.hasItems && (
                      <div className="mb-4">
                        <Label className="text-xs uppercase text-muted-foreground">Points de contrôle</Label>
                        {renderOrderedList(zone.key, 'items')}
                        <div className="flex gap-2">
                          <Input
                            placeholder="Ajouter un point (ex: Chambres froides)"
                            value={newItemInput[zone.id] || ''}
                            onChange={(e) => setNewItemInput(prev => ({ ...prev, [zone.id]: e.target.value }))}
                            onKeyPress={(e) => { if (e.key === 'Enter') { addZoneItem(zone.key, 'items', newItemInput[zone.id]); setNewItemInput(prev => ({ ...prev, [zone.id]: '' })); } }}
                            className="min-w-0 flex-1 h-11"
                          />
                          <button onClick={() => { addZoneItem(zone.key, 'items', newItemInput[zone.id]); setNewItemInput(prev => ({ ...prev, [zone.id]: '' })); }} className="h-9 px-3 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground text-sm"><Plus className="w-4 h-4" /></button>
                        </div>
                      </div>
                    )}

                    <div>
                      <Label className="text-xs uppercase text-muted-foreground">Options cliquables</Label>
                      {renderOrderedList(zone.key, zone.optionsField || 'options')}
                      <div className="flex gap-2">
                        <Input
                          placeholder="Ajouter une option (ex: Porte non verrouillée)"
                          value={newOptionInput[zone.id] || ''}
                          onChange={(e) => setNewOptionInput(prev => ({ ...prev, [zone.id]: e.target.value }))}
                          onKeyPress={(e) => { if (e.key === 'Enter') { addZoneItem(zone.key, zone.optionsField || 'options', newOptionInput[zone.id]); setNewOptionInput(prev => ({ ...prev, [zone.id]: '' })); } }}
                          className="min-w-0 flex-1 h-11"
                        />
                        <button onClick={() => { addZoneItem(zone.key, zone.optionsField || 'options', newOptionInput[zone.id]); setNewOptionInput(prev => ({ ...prev, [zone.id]: '' })); }} className="h-9 px-3 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground text-sm"><Plus className="w-4 h-4" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                Note: l&apos;ordre et les points ajoutés pour Restaurant / Cuisine / Sous-sols apparaissent
                dans le PDF avec le même style (libellé + valeur). Les en-têtes à puces du modèle sont conservés.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="remark-templates" className="bg-card border border-border rounded-lg">
            <AccordionTrigger className="px-6 py-4 hover:no-underline">
              <div className="flex items-center gap-3">
                <ClipboardList className="w-5 h-5 text-primary" />
                <span className="text-xl font-bold uppercase">Remarques Fréquentes</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-4">
              <p className="text-sm text-muted-foreground mb-4">
                Remarques proposées en un clic à l&apos;agent, dans la section Remarques / Incidents.
                Écrivez <code className="text-foreground">{'{reception}'}</code> pour insérer
                automatiquement le nom de la réception saisi en tête de rapport, et des points de
                suspension (…) là où l&apos;agent doit compléter.
              </p>
              <div className="space-y-5">
                {Object.entries(remarkTemplates || {}).map(([category, list]) => (
                  <div key={category} className="rounded-lg border border-border bg-zinc-900/40 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-bold text-foreground uppercase text-sm tracking-wide">{category}</h4>
                      <button
                        type="button"
                        title="Supprimer la catégorie"
                        onClick={() => removeRemarkCategory(category)}
                        className="p-1 rounded text-destructive hover:bg-zinc-700 text-xs"
                      >
                        Supprimer la catégorie
                      </button>
                    </div>
                    {(list || []).length === 0 ? (
                      <span className="text-xs text-muted-foreground">Liste vide</span>
                    ) : (
                      <div className="space-y-1.5 my-2">
                        {list.map((text, index) => (
                          <div key={text} className="flex items-center gap-2 bg-zinc-800 px-2 py-1.5 rounded text-xs">
                            <span className="text-muted-foreground w-5 shrink-0 tabular-nums">{index + 1}.</span>
                            <span className="flex-1 min-w-0 break-words">{text}</span>
                            <div className="flex items-center gap-0.5 shrink-0">
                              <button type="button" title="Monter" disabled={index === 0}
                                onClick={() => moveRemarkTemplate(category, index, -1)}
                                className="p-1 rounded hover:bg-zinc-700 disabled:opacity-30 disabled:pointer-events-none">
                                <ArrowUp className="w-3.5 h-3.5" />
                              </button>
                              <button type="button" title="Descendre" disabled={index === list.length - 1}
                                onClick={() => moveRemarkTemplate(category, index, 1)}
                                className="p-1 rounded hover:bg-zinc-700 disabled:opacity-30 disabled:pointer-events-none">
                                <ArrowDown className="w-3.5 h-3.5" />
                              </button>
                              <button type="button" title="Supprimer"
                                onClick={() => removeRemarkTemplate(category, text)}
                                className="p-1 rounded text-destructive hover:bg-zinc-700">
                                ✕
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2 mt-2">
                      <Input
                        placeholder="Ajouter une remarque (ex: Livraison des journaux.)"
                        value={newRemarkInput[category] || ''}
                        onChange={(e) => setNewRemarkInput(prev => ({ ...prev, [category]: e.target.value }))}
                        onKeyPress={(e) => { if (e.key === 'Enter') { addRemarkTemplate(category, newRemarkInput[category]); setNewRemarkInput(prev => ({ ...prev, [category]: '' })); } }}
                        className="min-w-0 flex-1 h-11"
                      />
                      <button
                        onClick={() => { addRemarkTemplate(category, newRemarkInput[category]); setNewRemarkInput(prev => ({ ...prev, [category]: '' })); }}
                        className="h-9 px-3 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground text-sm"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-5">
                <Input
                  placeholder="Nouvelle catégorie (ex: Travaux)"
                  value={newRemarkCategory}
                  onChange={(e) => setNewRemarkCategory(e.target.value)}
                  onKeyPress={(e) => { if (e.key === 'Enter') { addRemarkCategory(newRemarkCategory); setNewRemarkCategory(''); } }}
                  className="min-w-0 flex-1 h-11"
                />
                <button
                  onClick={() => { addRemarkCategory(newRemarkCategory); setNewRemarkCategory(''); }}
                  className="h-9 px-3 rounded-md bg-secondary hover:bg-secondary/80 text-secondary-foreground text-sm"
                >
                  Ajouter une catégorie
                </button>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="ops" className="bg-card border border-border rounded-lg">
            <AccordionTrigger className="px-6 py-4 hover:no-underline">
              <div className="flex items-center gap-3">
                <Download className="w-5 h-5 text-primary" />
                <span className="text-xl font-bold uppercase">Exports & Validation</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <Label>Mois export ZIP</Label>
                  <Input type="month" value={zipMonth} onChange={(e) => setZipMonth(e.target.value)} />
                </div>
                <div className="flex items-end">
                  <button onClick={handleExportMonthlyZip} className="w-full h-11 px-4 rounded-md bg-green-800 hover:bg-green-900 text-white font-semibold">
                    Export ZIP mensuel
                  </button>
                </div>
                <div className="flex items-end">
                  <button onClick={handleValidateReports} className="w-full h-11 px-4 rounded-md bg-secondary hover:bg-secondary/80 text-secondary-foreground font-semibold">
                    Valider avant export
                  </button>
                </div>
              </div>
              {validationResult && (
                <div className="rounded-lg border border-border bg-zinc-900/40 p-4">
                  <p className="font-semibold mb-2">{validationResult.count || 0} rapport(s) à vérifier</p>
                  <div className="space-y-2 max-h-48 overflow-y-auto text-sm text-muted-foreground">
                    {(validationResult.reports || []).map(item => (
                      <div key={item.id} className="border-b border-border/50 pb-2">
                        <span className="text-foreground">{item.date_start} - {item.agent_name}</span>: {item.warnings.join(', ')}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>

          {authMode === 'password' && (
          <AccordionItem value="login-audit" className="bg-card border border-border rounded-lg">
            <AccordionTrigger className="px-6 py-4 hover:no-underline">
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-primary" />
                <span className="text-xl font-bold uppercase">Connexions</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-4">
              <button onClick={fetchLoginAudit} className="h-10 px-4 rounded-md bg-secondary hover:bg-secondary/80 text-secondary-foreground font-medium mb-3">
                Actualiser
              </button>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {loginAudit.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Aucune tentative de connexion.</p>
                ) : loginAudit.map(entry => (
                  <div key={entry.id} className="p-3 rounded-md bg-zinc-900/50 border border-border text-sm">
                    <div className="flex flex-wrap gap-2 text-foreground">
                      <strong>{entry.success ? 'Réussite' : 'Échec'}</strong>
                      <span>{entry.username || '—'}</span>
                      <span className="text-muted-foreground">{entry.ip}</span>
                      <span className="text-muted-foreground">{entry.created_at ? formatDateTime24(entry.created_at, { dateStyle: 'short', timeStyle: 'short' }) : ''}</span>
                    </div>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
          )}

          <AccordionItem value="audit" className="bg-card border border-border rounded-lg">
            <AccordionTrigger className="px-6 py-4 hover:no-underline">
              <div className="flex items-center gap-3">
                <ClipboardList className="w-5 h-5 text-primary" />
                <span className="text-xl font-bold uppercase">Journal d'Audit</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-4">
              <button onClick={fetchAuditLog} className="h-10 px-4 rounded-md bg-secondary hover:bg-secondary/80 text-secondary-foreground font-medium mb-3">
                Actualiser
              </button>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {auditLog.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Aucune entrée d'audit pour le moment.</p>
                ) : auditLog.map(entry => (
                  <div key={entry.id} className="p-3 rounded-md bg-zinc-900/50 border border-border text-sm">
                    <div className="flex flex-wrap gap-2 text-foreground">
                      <strong>{entry.action}</strong>
                      <span>{entry.agent_name || 'Système'}</span>
                      <span className="text-muted-foreground">{entry.created_at ? formatDateTime24(entry.created_at, { dateStyle: 'short', timeStyle: 'short' }) : ''}</span>
                    </div>
                    {entry.report_id && <div className="text-xs text-muted-foreground">Rapport: {entry.report_id}</div>}
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Changement de mot de passe */}
          <AccordionItem value="password" className="bg-card border border-border rounded-lg">
            <AccordionTrigger className="px-6 py-4 hover:no-underline">
              <div className="flex items-center gap-3">
                <Key className="w-5 h-5 text-primary" />
                <span className="text-xl font-bold uppercase">Changer le Mot de Passe Admin</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-4">
              <div className="max-w-md space-y-4">
                <div>
                  <Label htmlFor="current-password">Mot de passe actuel</Label>
                  <Input
                    id="current-password"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Entrez le mot de passe actuel"
                  />
                </div>
                <div>
                  <Label htmlFor="new-password">Nouveau mot de passe</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="12 caractères, majuscule, chiffre, spécial"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm-password">Confirmer le nouveau mot de passe</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Répétez le nouveau mot de passe"
                  />
                </div>
                <button
                  onClick={handleChangePassword}
                  className="h-11 px-6 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                >
                  Changer le mot de passe
                </button>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Base de données */}
          <AccordionItem value="database" className="bg-card border border-border rounded-lg">
            <AccordionTrigger className="px-6 py-4 hover:no-underline">
              <div className="flex items-center gap-3">
                <Database className="w-5 h-5 text-primary" />
                <span className="text-xl font-bold uppercase">Base de données</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-4">
              <p className="text-sm text-muted-foreground mb-4">
                Exportez la base pour en télécharger une copie.
              </p>
              {migrationStatus && (
                <div className="mb-4 p-3 rounded-md border border-border bg-zinc-900/40 text-sm">
                  <p><strong>Base actuelle :</strong> PostgreSQL</p>
                  {migrationStatus.legacyFound && <p><strong>Ancienne DB détectée.</strong></p>}
                  {migrationStatus.migrated && <p><strong>Migration auto effectuée.</strong></p>}
                </div>
              )}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleExportDb}
                  data-testid="btn-export-db"
                  className="flex items-center gap-2 h-11 px-6 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                >
                  <Download className="w-4 h-4" />
                  Exporter la base
                </button>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="version" className="bg-card border border-border rounded-lg">
            <AccordionTrigger className="px-6 py-4 hover:no-underline">
              <div className="flex items-center gap-3">
                <BarChart3 className="w-5 h-5 text-primary" />
                <span className="text-xl font-bold uppercase">Version & Changelog</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-4">
              <p className="text-sm text-muted-foreground mb-3">Version actuelle: <strong className="text-foreground">{appInfo?.version || 'N/A'}</strong></p>
              <div className="space-y-3">
                {(appInfo?.changelog || []).map(item => (
                  <div key={item.version} className="p-3 rounded-md bg-zinc-900/50 border border-border">
                    <p className="font-semibold">v{item.version} - {item.date}</p>
                    <ul className="list-disc list-inside text-sm text-muted-foreground">
                      {(item.changes || []).map(change => <li key={change}>{change}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
    </>
  );
};

export default AdminPanel;
